import React, { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import { faGear } from "@fortawesome/pro-solid-svg-icons";

import {
	Avatar,
	Breadcrumb,
	Breadcrumbs,
	ButtonStandard,
	Chip,
	DropDownMenu,
	Icon,
	Pagination,
	Text,
	toast,
} from "@performyard/py-components";
import { WorkflowDetailTodoStatusFilterSchema } from "@performyard/service-contracts";
import type { WorkflowDetailTodoListItem, WorkflowDetailTodoStatusFilter } from "@performyard/service-contracts";

import PageHeading from "../../../components/PageHeading";
import ConfirmDangerModal from "./ConfirmDangerModal";
import ReadableWorkflowDetailsFilterChips, {
	convertWorkflowDetailsFiltersToReadableList,
} from "./ReadableWorkflowDetailsFilterChips";
import TodoFormModal from "./TodoFormModal";
import WorkflowDetailsFilterPanel, { WorkflowDetailsTodoFilters } from "./WorkflowDetailsFilterPanel";
import WorkflowDetailsTodosTable from "./WorkflowDetailsTodosTable";
import WorkflowProgressBar from "./WorkflowProgressBar";

import {
	useCancelWorkflow,
	useCheckWorkflowTodo,
	useRemoveWorkflowTodo,
	useSetWorkflowArchived,
	useWorkflowDetailTodos,
	useWorkflowDetails,
} from "../../../api/hooks";
import { useIsNewNavigation } from "../../../api/hooks/defs/navigation";

import { ErrorResponse } from "../../../api/services/defs/internal-api/internal-base-request";
import { describeWorkflowActionError } from "../../../api/services/defs/workflows";

import { parseWorkflowsOrigin } from "../../../utils/workflowsBreadcrumbs";

import InfoPageTemplate from "../../../pageTemplates/InfoPageTemplate";
import StandardToolbarActionRow from "../../../pageTemplates/InfoPageTemplate/StandardToolbarActionRow";
import useInfoPageTemplateQueryState from "../../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";
import WorkflowsInfoPanel from "../WorkflowsInfoPanel";
import { formatUtcDate } from "../dateFormatting";
import { WORKFLOWS_NAVIGATION_LINKS } from "../navigationLinks";
import {
	MAX_SEARCH_LENGTH,
	isNamedReference,
	isObjectIdString,
	isSerializedDate,
	sanitizePageNumber,
	sanitizeSortBy,
	sanitizeSortDirection,
	toDateOnlyParam,
} from "../todoFilterState";
import useWorkflowsPageGate from "../useWorkflowsPageGate";

const PAGE_SIZE = 25;

const isDetailTodoStatus = (value: unknown): value is WorkflowDetailTodoStatusFilter =>
	WorkflowDetailTodoStatusFilterSchema.safeParse(value).success;

const getErrorStatus = (error: unknown): number | undefined =>
	error instanceof ErrorResponse ? error.response?.status : undefined;

const normalizeDetailsFilters = (filters: Partial<WorkflowDetailsTodoFilters>): Partial<WorkflowDetailsTodoFilters> => {
	const normalized: Partial<WorkflowDetailsTodoFilters> = {};
	if (isNamedReference(filters.assignee)) {
		normalized.assignee = { id: filters.assignee.id, name: filters.assignee.name };
	}
	if (isDetailTodoStatus(filters.status)) {
		normalized.status = filters.status;
	}
	for (const key of ["dueDateStart", "dueDateEnd"] as const) {
		const value = filters[key];
		// Date instances come from the panel; "date-..." strings come from the URL and
		// are converted by the query-state hook after this runs.
		if (value instanceof Date || isSerializedDate(value)) {
			normalized[key] = value as Date;
		}
	}
	if (Array.isArray(filters.hasSignatureRequest) && filters.hasSignatureRequest.length > 0) {
		normalized.hasSignatureRequest = ["true"];
	}
	return normalized;
};

const WorkflowDetails = () => {
	const { workflowId = "" } = useParams<{ workflowId: string }>();
	const isNewNav = useIsNewNavigation();
	const navigate = useNavigate();
	const location = useLocation();

	const [isFiltersOpen, setIsFiltersOpen] = useState(false);
	const [isTodoFormOpen, setIsTodoFormOpen] = useState(false);
	const [todoBeingEdited, setTodoBeingEdited] = useState<WorkflowDetailTodoListItem | null>(null);
	const [todoBeingDeleted, setTodoBeingDeleted] = useState<WorkflowDetailTodoListItem | null>(null);
	const [isArchiveConfirmOpen, setIsArchiveConfirmOpen] = useState(false);
	const [isDeleteWorkflowConfirmOpen, setIsDeleteWorkflowConfirmOpen] = useState(false);

	const { isGateLoading, featureAvailable, canView, canManage, docSigningEnabled } = useWorkflowsPageGate();

	const {
		pageNumber,
		setPageNumber,
		sort,
		setSort,
		filters,
		setFilters,
		searchValue,
		debouncedSearchValue,
		setDebouncedSearch,
		handleQueryChange,
	} = useInfoPageTemplateQueryState<WorkflowDetailsTodoFilters>({
		defaultSortName: "due_date",
		defaultSortDirection: "asc",
		defaultPageNumber: 1,
		normalizeFilters: normalizeDetailsFilters,
	});

	const safePageNumber = sanitizePageNumber(pageNumber);

	const isValidWorkflowId = isObjectIdString(workflowId);
	const queriesEnabled = !isGateLoading && featureAvailable && canView && isValidWorkflowId;

	const {
		data: workflow,
		isLoading: isWorkflowLoading,
		isError: isWorkflowError,
		error: workflowError,
	} = useWorkflowDetails(workflowId, { enabled: queriesEnabled });

	const filterParams = {
		action: debouncedSearchValue?.slice(0, MAX_SEARCH_LENGTH) || undefined,
		actor: filters.assignee?.id,
		status: filters.status,
		dueDateStart: toDateOnlyParam(filters.dueDateStart),
		dueDateEnd: toDateOnlyParam(filters.dueDateEnd),
		hasSignatureRequest: (filters.hasSignatureRequest?.length ?? 0) > 0 || undefined,
	};

	const {
		data: todosData,
		isLoading: isTodosLoading,
		isError: isTodosError,
	} = useWorkflowDetailTodos(
		workflowId,
		{
			...filterParams,
			sortBy: sanitizeSortBy(sort.name),
			sortOrder: sanitizeSortDirection(sort.direction),
			limit: PAGE_SIZE,
			skip: (safePageNumber - 1) * PAGE_SIZE,
		},
		{ enabled: queriesEnabled && !!workflow }
	);

	const { mutate: checkTodo } = useCheckWorkflowTodo();
	const { mutate: removeTodo, isPending: isRemovingTodo } = useRemoveWorkflowTodo();
	const { mutate: setArchived, isPending: isSettingArchived } = useSetWorkflowArchived();
	const { mutate: deleteWorkflow, isPending: isDeletingWorkflow } = useCancelWorkflow();

	const totalCount = todosData?.totalCount ?? 0;
	const pageCount = Math.ceil(totalCount / PAGE_SIZE);

	// Deleting or completing the last row of the last page (page number lives in the
	// URL) would otherwise leave the query pointing past the end — an empty page with
	// no pager. Clamp back to the final page once the shrunken result arrives.
	// Uses handleQueryChange rather than setPageNumber so the clamp REPLACES the
	// history entry: it's a correction, not a navigation, and pushing would let Back
	// return to the out-of-range page and clamp forward again.
	useEffect(() => {
		if (!isTodosLoading && totalCount > 0 && safePageNumber > pageCount) {
			handleQueryChange({ pageNumber: pageCount });
		}
	}, [isTodosLoading, totalCount, safePageNumber, pageCount, handleQueryChange]);

	if (isGateLoading) {
		return null;
	}

	if (!featureAvailable) {
		return <Navigate to="/feature-disabled/" replace />;
	}

	if (!canView) {
		return <WorkflowsInfoPanel>Missing required permissions.</WorkflowsInfoPanel>;
	}

	// A malformed id, or a 404 from the details endpoint, means this workflow really
	// isn't there. Any other failure (500, auth, offline) is a loading problem — the
	// query disables the global error toast, so saying "not found" for those would
	// misreport an outage as a missing record.
	const isWorkflowMissing = !isValidWorkflowId || (isWorkflowError && getErrorStatus(workflowError) === 404);

	if (isWorkflowMissing) {
		return (
			<WorkflowsInfoPanel>
				Workflow not found.{" "}
				<Link to="/company/workflows" className="text-primary-7">
					Back to Company Workflows
				</Link>
			</WorkflowsInfoPanel>
		);
	}

	if (isWorkflowError) {
		return (
			<WorkflowsInfoPanel>There was a problem loading this workflow. Refresh to try again.</WorkflowsInfoPanel>
		);
	}

	const todos = todosData?.todos ?? [];

	const activeFilterChips = convertWorkflowDetailsFiltersToReadableList(filters);
	const hasActiveFilters = activeFilterChips.length > 0 || !!debouncedSearchValue;

	// A failed to-dos request must not read as "nothing matched" — that reports an
	// outage (or a rejected query) as a deliberately empty result and hides the real
	// problem, which is indistinguishable from a filter that genuinely excludes
	// everything.
	const getTodosEmptyStateText = (): string => {
		if (isTodosError) {
			return "There was a problem loading these to-dos. Refresh to try again.";
		}
		return hasActiveFilters ? "No to-dos match the applied filters" : "No To-Do’s Found";
	};
	const todosEmptyStateText = getTodosEmptyStateText();

	const pageTitle = workflow ? `${workflow.name} - Workflow` : "Workflow";

	const handleToggleDone = (todo: WorkflowDetailTodoListItem) => {
		checkTodo(
			{ todoId: todo._id, done: !todo.done },
			{
				onSuccess: () => toast.success(todo.done ? "To-Do marked incomplete" : "To-Do marked complete"),
				onError: (error) =>
					toast.error(describeWorkflowActionError(error, "There was an error updating the to-do.")),
			}
		);
	};

	const handleConfirmDeleteTodo = () => {
		if (!todoBeingDeleted) return;
		removeTodo(
			{ todoId: todoBeingDeleted._id },
			{
				onSuccess: () => {
					toast.success("To-Do deleted");
					setTodoBeingDeleted(null);
				},
				onError: (error) =>
					toast.error(describeWorkflowActionError(error, "There was an error deleting the to-do.")),
			}
		);
	};

	const handleSetArchived = (archived: boolean) => {
		setArchived(
			{ workflowId, archived },
			{
				onSuccess: () => {
					toast.success(archived ? "Workflow archived" : "Workflow reopened");
					setIsArchiveConfirmOpen(false);
				},
				onError: () => toast.error("There was an error updating the workflow."),
			}
		);
	};

	const handleConfirmDeleteWorkflow = () => {
		deleteWorkflow(
			{ workflowId },
			{
				onSuccess: () => {
					toast.success("Workflow deleted");
					navigate("/company/workflows/");
				},
				onError: (error) =>
					toast.error(describeWorkflowActionError(error, "There was an error deleting the workflow.")),
			}
		);
	};

	const origin = parseWorkflowsOrigin(location.search);
	const breadcrumbItems: Breadcrumb[] = [
		{ pageId: "breadcrumb-workflows-origin", name: origin.label, href: origin.from },
		{
			pageId: "breadcrumb-workflow-details-current",
			name: workflow?.name ?? "Workflow",
			href: location.pathname,
		},
	];

	const optionsMenu = canManage ? (
		<DropDownMenu
			testid="workflowOptionsMenu"
			bordered
			placement="bottom-end"
			options={[
				{
					key: "archive",
					label: workflow?.archived ? "Reopen" : "Archive",
					onClick: () => {
						if (workflow?.archived) {
							handleSetArchived(false);
						} else {
							setIsArchiveConfirmOpen(true);
						}
					},
				},
				{
					key: "delete",
					label: "Delete",
					onClick: () => setIsDeleteWorkflowConfirmOpen(true),
				},
			]}
		>
			<span className="flex items-center gap-1.5">
				Options
				<Icon icon={faGear} size={16} color="neutral-7" />
			</span>
		</DropDownMenu>
	) : null;

	const pageHeading = (
		<div className="flex flex-col gap-3">
			{isNewNav && <Breadcrumbs items={breadcrumbItems} />}
			<div className="flex items-start justify-between gap-4">
				<div className="flex min-w-0 items-center gap-3">
					{workflow?.subject && (
						<Avatar
							size={40}
							id={workflow.subject._id}
							name={workflow.subject.name}
							title={workflow.subject.name}
							testId="workflowSubjectAvatar"
						/>
					)}
					<div className="min-w-0">
						<div className="flex items-center gap-2">
							<PageHeading>{workflow?.name ?? ""}</PageHeading>
							{workflow?.archived && <Chip label="Archived" color="neutral" size={24} />}
						</div>
						{workflow && (
							<Text size="paragraph-3" weight="regular" color="neutral-7">
								{`Started: ${formatUtcDate(workflow.startDate, "large")}`}
							</Text>
						)}
					</div>
				</div>
				{optionsMenu && <div className="shrink-0">{optionsMenu}</div>}
			</div>
			{workflow && (
				<WorkflowProgressBar counts={workflow.todoStatusCounts} totalCount={workflow.totalTodoCount} />
			)}
		</div>
	);

	const actionButtons = canManage
		? [
				<ButtonStandard
					key="add-new-todo"
					data-testid="addNewTodoButton"
					color="primary"
					iconLeft={faPlus}
					dangerouslySetClassName="w-auto"
					onClick={() => {
						setTodoBeingEdited(null);
						setIsTodoFormOpen(true);
					}}
				>
					Add New To-Do
				</ButtonStandard>,
			]
		: [];

	const toolbarActionRow = (
		<StandardToolbarActionRow
			searchInput={searchValue}
			onSearchInputChange={setDebouncedSearch}
			onFilterButtonPress={() => setIsFiltersOpen(!isFiltersOpen)}
			activeFiltersCount={activeFilterChips.length}
			searchPlaceholder="Search to-do’s..."
		/>
	);

	const toolbarInformationRow = <ReadableWorkflowDetailsFilterChips filters={filters} setFilters={setFilters} />;

	const drawer = (
		<WorkflowDetailsFilterPanel
			isOpen={isFiltersOpen}
			onClose={() => setIsFiltersOpen(false)}
			filters={filters}
			onApplyFilters={(newFilters) => setFilters(newFilters)}
		/>
	);

	const mainContent = (
		<div>
			<WorkflowDetailsTodosTable
				todos={todos}
				isLoading={isWorkflowLoading || isTodosLoading}
				onSortChange={setSort}
				activeSort={sanitizeSortBy(sort.name)}
				activeSortDirection={sanitizeSortDirection(sort.direction)}
				emptyStateText={todosEmptyStateText}
				canManage={canManage}
				onToggleDone={handleToggleDone}
				onEdit={(todo) => {
					setTodoBeingEdited(todo);
					setIsTodoFormOpen(true);
				}}
				onDelete={(todo) => setTodoBeingDeleted(todo)}
			/>
			{pageCount > 1 && (
				<div data-testid="paginationContainer" className="mt-4 flex justify-center">
					<Pagination currentPage={safePageNumber} pageCount={pageCount} onPageChange={setPageNumber} />
				</div>
			)}
		</div>
	);

	return (
		<>
			<InfoPageTemplate
				actionButtons={actionButtons}
				toolbarActionRow={toolbarActionRow}
				toolbarInformationRow={toolbarInformationRow}
				drawer={drawer}
				mainContent={mainContent}
				pageTitle={pageTitle}
				pageHeading={pageHeading}
				navigationLinks={WORKFLOWS_NAVIGATION_LINKS}
				activeNavigationLinkKey="all-workflows"
			/>

			<TodoFormModal
				workflowId={workflowId}
				isOpen={isTodoFormOpen}
				onClose={() => {
					setIsTodoFormOpen(false);
					setTodoBeingEdited(null);
				}}
				todo={todoBeingEdited}
				allowSignature={docSigningEnabled}
				subject={workflow?.subject ?? null}
			/>

			<ConfirmDangerModal
				isOpen={!!todoBeingDeleted}
				onClose={() => setTodoBeingDeleted(null)}
				title="Delete To-Do?"
				message={
					todoBeingDeleted?.hasSignatureRequest
						? "This will also cancel the document signing request attached to this to-do. This can't be undone."
						: "Are you sure you want to delete this to-do? This action is permanent and cannot be undone."
				}
				confirmLabel="Yes, Delete To-Do"
				confirmTestId="confirmDeleteTodoButton"
				disabled={isRemovingTodo}
				onConfirm={handleConfirmDeleteTodo}
			/>

			<ConfirmDangerModal
				isOpen={isArchiveConfirmOpen}
				onClose={() => setIsArchiveConfirmOpen(false)}
				title="Archive Workflow?"
				message="Are you sure you want to archive this workflow?"
				confirmLabel="Yes, Archive"
				confirmTestId="confirmArchiveWorkflowButton"
				disabled={isSettingArchived}
				onConfirm={() => handleSetArchived(true)}
			/>

			<ConfirmDangerModal
				isOpen={isDeleteWorkflowConfirmOpen}
				onClose={() => setIsDeleteWorkflowConfirmOpen(false)}
				title="Delete Workflow?"
				message="The workflow and all of its to-dos (including any signature requests) will be permanently deleted. This cannot be undone."
				confirmLabel="Delete Workflow"
				confirmTestId="confirmDeleteWorkflowButton"
				disabled={isDeletingWorkflow}
				onConfirm={handleConfirmDeleteWorkflow}
			/>
		</>
	);
};

export default WorkflowDetails;
